import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable } from "rxjs";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import {
  Product,
  ProductCreate,
  ProductLight,
  ProductUpdateVariations
} from "../models/product";
import { values, round, pickBy, omitBy, isUndefined, first } from "lodash-es";
import { map } from "rxjs/operators";
import { HTTP_CONTEXT_TOKEN_NO_CACHE } from "src/app/core/http-context-tokens";

@Injectable()
export class ProductBackend {
  constructor(private http: HttpClient) {}

  load(
    productId: string,
    requestProductCoefficient: number,
    rateTableId: string | undefined
  ): Observable<Product> {
    return this.http
      .get<Product>(
        `${environment.snoots.api.healthcare}/products/${productId}`
      )
      .pipe(
        map(
          (x) => <Product>extendDoc(x, requestProductCoefficient, rateTableId)
        )
      );
  }

  loadLight(
    productId: string,
    requestProductCoefficient?: number,
    rateTableId?: string | undefined
  ): Observable<ProductLight> {
    return this.http
      .get<Product>(
        `${environment.snoots.api.healthcare}/products/${productId}`,
        {
          params: {
            light: true
          }
        }
      )
      .pipe(map((x) => extendDoc(x, requestProductCoefficient, rateTableId)));
  }

  loadLightNoCache(
    productId: string,
    requestProductCoefficient?: number,
    rateTableId?: string | undefined
  ): Observable<ProductLight> {
    return this.http
      .get<Product>(
        `${environment.snoots.api.healthcare}/products/${productId}`,
        {
          headers: {
            ["snt-no-cache"]: "true"
          },
          params: {
            light: true
          },
          context: new HttpContext().set(HTTP_CONTEXT_TOKEN_NO_CACHE, true)
        }
      )
      .pipe(map((x) => extendDoc(x, requestProductCoefficient, rateTableId)));
  }

  loadLightByBarcode(
    barCode: string,
    requestProductCoefficient?: number,
    rateTableId?: string
  ): Observable<ProductLight | undefined> {
    return this.http
      .get<PaginatedResult<Product>>(
        `${environment.snoots.api.healthcare}/products`,
        {
          params: {
            light: true,
            barcode: barCode
          }
        }
      )
      .pipe(
        map((response) => first(response.docs)),
        map((x: ProductLight | undefined) =>
          x ? extendDoc(x, requestProductCoefficient, rateTableId) : undefined
        )
      );
  }

  loadLightByBarcodeNoCache(
    barCode: string,
    requestProductCoefficient?: number,
    rateTableId?: string
  ): Observable<ProductLight | undefined> {
    return this.http
      .get<PaginatedResult<Product>>(
        `${environment.snoots.api.healthcare}/products`,
        {
          params: {
            light: true,
            barcode: barCode
          },
          context: new HttpContext().set(HTTP_CONTEXT_TOKEN_NO_CACHE, true)
        }
      )
      .pipe(
        map((response) => first(response.docs)),
        map((x: ProductLight | undefined) =>
          x ? extendDoc(x, requestProductCoefficient, rateTableId) : undefined
        )
      );
  }

  search(
    request: PageRequest,
    name: string
  ): Observable<PaginatedResult<Product>> {
    return this.http.get<PaginatedResult<Product>>(
      `${environment.snoots.api.healthcare}/products`,
      {
        params: pickBy({
          p: request.page,
          s: request.pageSize,
          name
        })
      }
    );
  }

  searchByNameAllLight(name: string): Observable<ProductLight[]> {
    return this.searchAllLight({ name });
  }

  searchByBarcodeAllLight(barcode: string): Observable<ProductLight[]> {
    return this.searchAllLight({ barcode });
  }

  private searchAllLight({
    name,
    barcode
  }: {
    name?: string;
    barcode?: string;
  }): Observable<ProductLight[]> {
    return this.http
      .get<PaginatedResult<ProductLight>>(
        `${environment.snoots.api.healthcare}/products`,
        {
          params: omitBy(
            {
              p: 1,
              s: 10000,
              light: true,
              name,
              barcode
            },
            isUndefined
          )
        }
      )
      .pipe(map((response) => response.docs));
  }

  searchLight(
    request: PageRequest,
    name: string
  ): Observable<PaginatedResult<ProductLight>> {
    return this.http.get<PaginatedResult<ProductLight>>(
      `${environment.snoots.api.healthcare}/products`,
      {
        params: pickBy({
          p: request.page,
          s: request.pageSize,
          light: true,
          name
        })
      }
    );
  }

  list(
    request: PageRequest & {
      select?: string | undefined;
    }
  ): Observable<PaginatedResult<Product>> {
    return this.http.get<PaginatedResult<Product>>(
      `${environment.snoots.api.healthcare}/products`,
      {
        params: {
          p: request.page,
          s: request.pageSize,
          ...request.filterValues,
          select: request.select
        }
      }
    );
  }

  listForRateTable(
    request: PageRequest,
    requestProductCoefficient: number,
    rateTableId: string
  ): Observable<PaginatedResult<Product>> {
    return this.http
      .get<PaginatedResult<Product>>(
        `${environment.snoots.api.healthcare}/products`,
        {
          params: {
            p: request.page,
            s: request.pageSize,
            ...request.filterValues
          }
        }
      )
      .pipe(
        map((result) => {
          return {
            ...result,
            docs: result.docs.map(
              (x) =>
                <Product>extendDoc(x, requestProductCoefficient, rateTableId)
            )
          };
        })
      );
  }

  @UserNotify({
    successMessage: marker("Prodotto creato"),
    errorMessage: marker("Impossibile creare il Prodotto"),
    progressIndeterminate: true,
    progressMessage: marker("Crezione prodotto")
  })
  create(payload: ProductCreate): Observable<Product> {
    return this.http.post<Product>(
      `${environment.snoots.api.healthcare}/products`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Prodotto aggiornato"),
    errorMessage: marker("Impossibile aggiornare il prodotto")
  })
  update(id: string, request: Partial<Product>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/products/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Variazione aggiornata"),
    errorMessage: marker("Impossibile aggiornare la variazione")
  })
  updateVariation(
    id: string,
    payload: ProductUpdateVariations
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/products/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Prodotto disattivato"),
    errorMessage: marker("Impossibile disattivare il prodotto")
  })
  disabled(id: string, request: Partial<Product>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/products/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Prodotto eliminato"),
    errorMessage: marker("Impossibile eliminare il prodotto")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.healthcare}/products/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Prodotti esportati in Excel"),
    errorMessage: marker("Impossibile esportare i prodotti"),
    progressIndeterminate: true,
    progressMessage: marker("Esportazione prodotti in corso")
  })
  exportMassiveExcell(productIds: string[]): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.healthcare}/products/exports`,
      { productIds: productIds }
    );
  }
}

function extendDoc(
  product: Product | ProductLight,
  requestProductCoefficient?: number,
  rateTableId?: string
): Product | ProductLight {
  let calculatedPrice = product.unitPrice;
  let calculatedMaxPrice = product.maxUnitPrice;

  let variationPrice: number | undefined = undefined;
  let isPriceModified = false;

  if (
    rateTableId &&
    (<Product>product).variations &&
    values((<Product>product).variations).length > 0
  ) {
    const variation = values((<Product>product).variations).find(
      (x) => x.rateTableId === rateTableId
    );
    if (variation && variation.enabled) {
      variationPrice = variation.price;
    }
  }

  if (variationPrice) {
    calculatedPrice = variationPrice;
    calculatedMaxPrice = variationPrice;
    isPriceModified = true;
  } else {
    if (requestProductCoefficient) {
      calculatedPrice = round(
        product.unitPrice * (1 + requestProductCoefficient),
        2
      );
      calculatedMaxPrice = product.maxUnitPrice
        ? round(product.maxUnitPrice * (1 + requestProductCoefficient), 2)
        : 0;
    }
  }

  let totalPrice = product.unitPrice * (1 + product.vat);
  let totalMaxPrice = product.maxUnitPrice
    ? product.maxUnitPrice * (1 + product.vat)
    : 0;

  if (requestProductCoefficient) {
    totalPrice = round(
      product.unitPrice * (1 + product.vat) * (1 + requestProductCoefficient),
      2
    );
    totalMaxPrice = product.maxUnitPrice
      ? round(
          product.maxUnitPrice *
            (1 + product.vat) *
            (1 + requestProductCoefficient),
          2
        )
      : 0;
  }

  return {
    ...product,
    totalPrice,
    totalMaxPrice,
    calculatedPrice,
    calculatedMaxPrice,
    isPriceModified
  };
}
